import { Area, AreaChart, YAxis } from "recharts";

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@suns/design-system/src/components/Chart/Chart";

import { generateChartTicks } from "@/shared/utils/helper-functions";

import { roundToSignificantDigit } from "@/shared/utils/helper-functions";
import { ChartConfig, Flex } from "@suns/design-system";
import { CURRENT_DATE } from "@/shared/const";
import { CartesianGrid, XAxis, ReferenceLine, Dot } from "recharts";
import dayjs from "dayjs";
import { theme } from "@/shared/theme";

type ChartData = {
  snapshotDate: string;
  [key: string]: string | number | null;
};

type TooltipMetricConfig = {
  formatter: (value: number) => string;
  relatedMetrics?: Array<{
    key: string;
    label: string;
  }>;
};

type DataAreaChartProps = {
  chartData: ChartData[];
  chartConfig: ChartConfig;
  tooltipConfig?: {
    [key: string]: TooltipMetricConfig;
  };
  dateRange: "monthly" | "annual";
  yAxisConfig?: {
    minValue?: number;
    maxValue?: number;
    minTickCount?: number;
    formatter?: (value: number) => string;
  };
};

export function DataAreaChart({
  chartData,
  chartConfig,
  tooltipConfig,
  dateRange,
  yAxisConfig,
}: DataAreaChartProps) {
  const allChartValues = chartData.flatMap((d) =>
    Object.keys(chartConfig).map((key) => Number(d[key]))
  );

  const minTicksValue = roundToSignificantDigit(
    Math.min(...allChartValues) * 1.5
  );
  const maxTicksValue = roundToSignificantDigit(
    Math.max(...allChartValues) * 1.5
  );

  const ticks = generateChartTicks(
    yAxisConfig?.minValue ?? minTicksValue < 0 ? minTicksValue : 0,
    yAxisConfig?.maxValue ?? maxTicksValue > 0
      ? maxTicksValue
      : Math.abs(minTicksValue),
    yAxisConfig?.minTickCount ?? 8
  );

  function gradientOffset() {
    const dataMax = Math.max(...chartData.map((d) => Number(d?.trv ?? d?.prv)));
    const dataMin = Math.min(...chartData.map((d) => Number(d?.trv ?? d?.prv)));

    if (dataMax <= 0) return 0;
    if (dataMin >= 0) return 1;

    return dataMax / (dataMax - dataMin);
  }

  const off = gradientOffset();

  return (
    <ChartContainer config={chartConfig} className="h-[400px] w-full md:h-full">
      <AreaChart
        accessibilityLayer
        data={chartData}
        margin={{
          top: 12,
          left: 20,
          right: 12,
        }}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="snapshotDate"
          tickLine={false}
          axisLine={false}
          tickMargin={12}
          tickFormatter={(value) => {
            const isAnnual = dateRange === "annual";
            const dateFormat = isAnnual ? "MMM YYYY" : "MMM DD YYYY";
            const isPresent = value === CURRENT_DATE.format("MMM DD YYYY");
            return isPresent
              ? "Today"
              : dayjs(value, dateFormat).format(isAnnual ? "'YY" : "MMM YY");
          }}
          strokeWidth={2}
        />
        <YAxis
          width={30}
          minTickGap={12}
          interval={0}
          type="number"
          domain={[
            yAxisConfig?.minValue ?? minTicksValue,
            yAxisConfig?.maxValue ?? maxTicksValue,
          ]}
          ticks={ticks}
          tickFormatter={(value) => {
            return value.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
              notation: "compact",
              compactDisplay: "short",
            });
          }}
          tickLine={false}
        />
        <ReferenceLine
          y={0}
          stroke={theme.colors.gray[800]}
          strokeWidth={1.5}
          strokeOpacity={0.65}
        />

        <ChartLegend content={<ChartLegendContent hideLabel={true} />} />

        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              formatter={(value, name, props) => {
                const metricConfig = tooltipConfig?.[name];
                const chartMetricConfig = chartConfig[name];

                if (!chartMetricConfig) return null;

                return (
                  <Flex direction="down" justify="between">
                    <Flex gap="sm">
                      <span className="text-muted-foreground">
                        {chartMetricConfig.label}
                      </span>
                      <span className="font-mono font-medium tabular-nums text-foreground">
                        {metricConfig?.formatter?.(Number(value)) ?? value}
                      </span>
                    </Flex>
                    {metricConfig?.relatedMetrics?.map((metric) => (
                      <Flex key={metric.key} gap="sm">
                        <span className="text-muted-foreground">
                          {metric.label}
                        </span>
                        <span className="font-mono font-medium tabular-nums text-foreground">
                          {metricConfig.formatter(
                            Number(props.payload[metric.key])
                          )}
                        </span>
                      </Flex>
                    ))}
                  </Flex>
                );
              }}
            />
          }
        />

        <defs>
          {Object.keys(chartConfig).map((key) => (
            <linearGradient
              key={key}
              id={`gradient${key}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset={off}
                stopColor={theme.colors.green[600]}
                stopOpacity={1}
              />
              <stop
                offset={off}
                stopColor={theme.colors.red[600]}
                stopOpacity={1}
              />
            </linearGradient>
          ))}
        </defs>
        {Object.entries(chartConfig).map(([key], index) => (
          <Area
            key={key + index}
            dataKey={key}
            type="monotone"
            baseValue={0}
            stroke={`url(#gradient${key})`}
            strokeWidth={2}
            fillOpacity={0.2}
            fill={`url(#gradient${key})`}
            animationDuration={900}
            connectNulls={true}
            dot={({ payload, ...props }) => {
              const value = payload[key];
              const color =
                value > 0 ? theme.colors.green[600] : theme.colors.red[600];
              return (
                <Dot
                  key={`${key}-${props.cx}-${props.cy}`}
                  r={3}
                  cx={props.cx}
                  cy={props.cy}
                  fill={color}
                  stroke={color}
                />
              );
            }}
            activeDot={({ payload, ...props }) => {
              const value = payload[key];
              const color =
                value > 0 ? theme.colors.green[600] : theme.colors.red[600];
              return (
                <Dot
                  key={`${key}-${props.cx}-${props.cy}`}
                  r={6}
                  cx={props.cx}
                  cy={props.cy}
                  fill={color}
                  stroke={color}
                />
              );
            }}
          />
        ))}
      </AreaChart>
    </ChartContainer>
  );
}
